/**
 * プロジェクト内アンビエント背景。
 *
 * バナー色から導出した低アルファのアクセント（getProjectAmbient の accents）を
 * 大きな radial-gradient blob 3 枚として敷き、84〜132 秒スケールでゆっくり
 * ドリフトさせる。「動いている」と意識されない程度の呼吸感が狙いなので、
 * アニメーションは transform / opacity のみ（コンポジタ処理で軽い）。
 * filter: blur() はグラデーション自体が十分柔らかいうえ負荷が高いため使わない。
 *
 * 親要素に isolation: isolate があることを前提に z-index: -1 で背面に敷く
 * （親の背景より上・コンテンツより下に収まり、ページ背面には抜けない）。
 */
import React from "react";
import styled, { css, keyframes } from "styled-components";

export interface ProjectAmbientBackgroundProps {
  /** getProjectAmbient() が返す低アルファ色（先頭 / 中央 / 末尾） */
  accents: [string, string, string];
  /**
   * viewport: ProjectWrapper 直下でビューポート全面に固定
   * container: 不透明パネル（クイックタブ等）の内側いっぱいに敷く
   */
  variant?: "viewport" | "container";
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const driftA = keyframes`
  from { transform: translate3d(0, 0, 0) scale(1); opacity: 0.75; }
  to { transform: translate3d(5%, 4%, 0) scale(1.12); opacity: 1; }
`;

const driftB = keyframes`
  from { transform: translate3d(0, 0, 0) scale(1.08); opacity: 1; }
  to { transform: translate3d(-5%, -3%, 0) scale(1); opacity: 0.72; }
`;

const driftC = keyframes`
  from { transform: translate3d(-3%, 2%, 0) scale(1); opacity: 0.7; }
  to { transform: translate3d(3%, -2%, 0) scale(1.1); opacity: 1; }
`;

const AmbientRoot = styled.div<{ $variant: "viewport" | "container" }>`
  position: ${({ $variant }) =>
    $variant === "viewport" ? "fixed" : "absolute"};
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  /* プロジェクト切替（key 変更で再マウント）時に色をなめらかに立ち上げる */
  animation: ${fadeIn} 800ms ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const blobBase = css`
  position: absolute;
  will-change: transform, opacity;
`;

/* バナーの始点色（左上）。バナーが左→右に流れる向きと呼応させる */
const BlobLead = styled.div`
  ${blobBase}
  top: -32%;
  left: -28%;
  width: 105%;
  height: 95%;
  background: radial-gradient(
    ellipse at center,
    var(--ambient-accent-1) 0%,
    transparent 66%
  );
  animation: ${driftA} 84s ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* バナーの中間色（中央やや上をゆっくり横断） */
const BlobMid = styled.div`
  ${blobBase}
  top: 8%;
  left: 12%;
  width: 92%;
  height: 84%;
  background: radial-gradient(
    ellipse at center,
    var(--ambient-accent-2) 0%,
    transparent 66%
  );
  animation: ${driftC} 132s ease-in-out infinite alternate;
  animation-delay: -40s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* バナーの終点色（右下） */
const BlobTail = styled.div`
  ${blobBase}
  bottom: -34%;
  right: -30%;
  width: 110%;
  height: 95%;
  background: radial-gradient(
    ellipse at center,
    var(--ambient-accent-3) 0%,
    transparent 66%
  );
  animation: ${driftB} 108s ease-in-out infinite alternate;
  animation-delay: -70s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ProjectAmbientBackground: React.FC<ProjectAmbientBackgroundProps> = ({
  accents,
  variant = "viewport",
}) => {
  const cssVars = {
    "--ambient-accent-1": accents[0],
    "--ambient-accent-2": accents[1],
    "--ambient-accent-3": accents[2],
  } as React.CSSProperties;

  return (
    <AmbientRoot
      key={accents.join("|")}
      $variant={variant}
      style={cssVars}
      aria-hidden="true"
    >
      <BlobLead />
      <BlobMid />
      <BlobTail />
    </AmbientRoot>
  );
};

export default ProjectAmbientBackground;
